/* eslint-disable react/jsx-wrap-multilines */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Tooltip } from '@mui/material';
import get from 'lodash/get';

import ReactFlagsSelect from 'react-flags-select';
import ReactPhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

import ErrorPanel from 'components/ErrorPanel';
import { ROUTES } from 'enum';
import { setCurrentCustomer } from 'store/customers/actions';
import { isLoadingSelector } from 'store/user/selectors';

import './orgInfoSubForm.scss';

function OrgInfoSubForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const curCustomer = useSelector((state) => state.customers.currentCustomer);
  const isLoading = useSelector(isLoadingSelector);
  const [companyName, setCompanyName] = useState(() => get(curCustomer, 'name', ''));
  const [address, setAddress] = useState(() => get(curCustomer, 'address.line1', ''));
  const [country, setCountry] = useState(() => get(curCustomer, 'address.country', 'US').toUpperCase());
  const [city, setCity] = useState(() => get(curCustomer, 'address.city', ''));
  const [zipCode, setZipCode] = useState(() => get(curCustomer, 'address.postal_code', ''));
  const [stateValue, setStateValue] = useState(() => get(curCustomer, 'address.state', ''));
  const [name, setName] = useState(() => get(curCustomer, 'contact.name', ''));
  const [phone, setPhone] = useState(() => get(curCustomer, 'contact.phone', ''));
  const [unitedError, setUnitedError] = useState('');

  const isValid = () => {
    const regex = /^\+\d+\s*(\(\d+\))?[- \d]+$/;
    if (!phone) {
      setUnitedError('Phone Number Is Required');
      return false;
    }
    if (!regex.test(phone) || !isValidPhoneNumber(phone)) {
      setUnitedError('Invalid Phone Number');
      return false;
    }
    return true;
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!isValid()) {
      return;
    }

    const customer = {
      address: {
        city,
        country,
        line1: address,
        postal_code: zipCode,
        state: stateValue,
      },
      contact: {
        name,
        phone,
      },
      name: companyName,
    };
    dispatch(setCurrentCustomer(customer));
    history.push(ROUTES.CONTRACT);
  };

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;
    setUnitedError('');
    if (fieldName === 'companyName') {
      setCompanyName(value);
    } else if (fieldName === 'address') {
      setAddress(value);
    } else if (fieldName === 'city') {
      setCity(value);
    } else if (fieldName === 'state') {
      setStateValue(value);
    } else if (fieldName === 'zipCode') {
      setZipCode(value);
    } else if (fieldName === 'name') {
      setName(value);
    }
  };

  return (
    <form onSubmit={onSubmit} className="form-container">
      {unitedError && (
        <ErrorPanel
          message={unitedError}
          buttonClickHandler={() => setUnitedError('')}
          key={unitedError}
          dataTestId="signup-org-error"
        />
      )}
      <div className="company-info-form">
        <Grid container columnSpacing={3.75} rowSpacing={0}>
          <Grid item xs={12}>
            <label>
              <span className="labelForm">COMPANY NAME</span>
              <input
                type="text"
                placeholder="Enter Company Name"
                className="inputField"
                onChange={handleChange}
                name="companyName"
                value={companyName}
                maxLength={250}
                required
              />
            </label>
          </Grid>
          <Grid item xs={12}>
            <label>
              <span className="labelForm">STREET ADDRESS</span>
              <input
                type="text"
                placeholder="Enter Street Address"
                className="inputField"
                onChange={handleChange}
                name="address"
                value={address}
                required
              />
            </label>
          </Grid>
        </Grid>
        <Grid container columnSpacing={3.75} rowSpacing={0}>
          <Grid item xs={12} md={6}>
            <label>
              <span className="labelForm">COUNTRY</span>
              <ReactFlagsSelect
                searchable
                selected={country}
                onSelect={(countryCode) => setCountry(countryCode)}
                optionsSize={14}
                className="countrySelect"
              />
            </label>
          </Grid>
          <Grid item xs={12} md={6}>
            <label>
              <span className="labelForm">CITY</span>
              <input
                type="text"
                placeholder="Enter City"
                className="inputField"
                onChange={handleChange}
                name="city"
                value={city}
                required
              />
            </label>
          </Grid>
        </Grid>
        <Grid container columnSpacing={3.75} rowSpacing={0}>
          <Grid item xs={12} md={6}>
            <label>
              <span className="labelForm">STATE</span>
              <input
                type="text"
                placeholder="Enter state"
                className="inputField"
                onChange={handleChange}
                name="state"
                value={stateValue}
                required
              />
            </label>
          </Grid>
          <Grid item xs={12} md={6}>
            <label>
              <span className="labelForm">ZIPCODE</span>
              <input
                type="text"
                placeholder="Enter Zipcode"
                className="inputField"
                onChange={handleChange}
                minLength={5}
                maxLength={6}
                name="zipCode"
                value={zipCode}
                required
              />
            </label>
          </Grid>
        </Grid>
      </div>
      <div className="contact-form">
        <h4 className="title">Administrative Contact</h4>
        <div className="divider" />
        <Grid container columnSpacing={3.75} rowSpacing={0}>
          <Grid item xs={12} md={6}>
            <label>
              <span className="labelForm">NAME</span>
              <input
                type="text"
                placeholder="Enter Name"
                className="inputField"
                onChange={handleChange}
                name="name"
                value={name}
                required
              />
            </label>
          </Grid>
          <Grid item xs={12} md={6}>
            <label>
              <span className="labelForm">PHONE</span>
              <Tooltip
                placement="right"
                enterDelay={250}
                title={
                  <React.Fragment>
                    Please enter your phone number in the international format, for example:&nbsp;
                    <strong> +1 123-456-7890 </strong>
                  </React.Fragment>
                }
              >
                <div className="input-container">
                  <ReactPhoneInput
                    defaultCountry="US"
                    name="phone"
                    value={phone}
                    placeholder="Enter Number"
                    onChange={(value) => setPhone(value)}
                    required
                  />
                </div>
              </Tooltip>
            </label>
          </Grid>
        </Grid>
      </div>
      <div className="submit-button-container">
        <Button fullWidth sx={{ fontSize: 18, p: 2 }} type="submit" variant="contained" disabled={isLoading}>
          Next
        </Button>
      </div>
    </form>
  );
}

export default OrgInfoSubForm;
